import { SearchResultItem } from '../../types';
import { SearchResultSection } from '../result-section';
import { type LocaleCode, useTranslations } from '@ifixit/i18n';
import { useSearchContext } from '../search-provider';
import { getSearchUrl } from '../../utils';
import { Doctype } from '../../types/indices';

function ResultSectionsList({
   results,
   totalHits,
   query,
   currency,
   localeCode,
   storeCode,
}: {
   results: Record<Doctype, SearchResultItem[]>;
   totalHits: Record<Doctype, number>;
   query: string;
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   const doctypes = Object.keys(results) as Doctype[];
   return (
      <div className="pb-2 sm:pb-4">
         {doctypes.map(
            doctype =>
               results[doctype]?.length > 0 && (
                  <SearchResultSection
                     key={doctype}
                     doctype={doctype}
                     hits={results[doctype]}
                     totalHits={totalHits[doctype] || results[doctype].length}
                     query={query}
                     currency={currency}
                     localeCode={localeCode}
                     storeCode={storeCode}
                  />
               )
         )}
         <div className="sticky bottom-0 h-6 -mt-6 bg-gradient-to-t from-white to-transparent" />
      </div>
   );
}

/**
 * Footer with "See All Results" button
 */
export function SeeAllResultsFooter() {
   const { query, hasResults } = useSearchContext();
   const t = useTranslations('Search.actions');

   if (!hasResults) {
      return null;
   }
   return (
      <div className="sticky bottom-0 bg-white px-4 pb-5 self-stretch">
         <a
            href={getSearchUrl({ searchStr: query })}
            className="flex justify-center items-center gap-2 self-stretch py-2 px-4 h-9 rounded-xl bg-brand-500 !text-white text-center text-sm font-[510] w-full hover:bg-brand-600 !no-underline !hover:no-underline"
         >
            {t('seeAllResults')}
         </a>
      </div>
   );
}

/**
 * Search content component for the dropdown body
 *
 * This component uses the search context to access search state and results.
 * It automatically displays the appropriate content based on search state.
 */
export function SearchContent({
   currency,
   localeCode,
   storeCode,
}: {
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   const { hasResults, results, totalHits, query, hasSearched } = useSearchContext();

   if (hasResults) {
      return (
         <ResultSectionsList
            results={results}
            totalHits={totalHits}
            query={query}
            currency={currency}
            localeCode={localeCode}
            storeCode={storeCode}
         />
      );
   }

   if (hasSearched) {
      return <NoResultsMessage query={query} />;
   }

   return null;
}

function NoResultsMessage({ query }: { query: string }) {
   const t = useTranslations('Search.status');
   return (
      <p className="w-full text-center py-4" data-testid="no-search-results">
         {t('noResults', { query })}
      </p>
   );
}
